/* global theme */

if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
    constructor() {
      super();
      this.togglePanel = this.togglePanel.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('.cart-discount__panel');
      this.form = this.querySelector('.cart-discount__form');
      this.input = this.querySelector('input[name="discount"]');
      this.submitBtn = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('.cart-discount__error');
      this.toggle = this.closest('.cart-drawer__summary-inner')?.querySelector('.js-cart-discount-toggle');

      this.setPanelCollapsed(true);

      if (this.toggle) {
        this.toggle.addEventListener('click', this.togglePanel);
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit);
      }

      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      if (this.toggle) {
        this.toggle.removeEventListener('click', this.togglePanel);
      }

      if (this.form) {
        this.form.removeEventListener('submit', this.handleSubmit);
      }

      this.removeEventListener('click', this.handleClick);
    }

    /**
     * Currently applied discount codes from the rendered cart.
     * @returns {string[]}
     */
    existingCodes() {
      return (this.dataset.codes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }

    /**
     * Toggles the sliding coupon field.
     */
    togglePanel() {
      if (this.classList.contains('is-open')) {
        this.close();
      } else {
        this.open();
      }
    }

    /**
     * Slides the coupon field open and focuses the input.
     */
    open() {
      this.classList.add('is-open');
      this.setPanelCollapsed(false);
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
      this.clearError();
      this.input?.focus();
      this.recalculateHeights();
    }

    /**
     * Slides the coupon field closed.
     */
    close() {
      this.classList.remove('is-open');
      this.setPanelCollapsed(true);
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      this.recalculateHeights();
    }

    /**
     * @param {boolean} collapsed
     */
    setPanelCollapsed(collapsed) {
      if (this.panel) this.panel.inert = collapsed;
      if (this.input) this.input.tabIndex = collapsed ? -1 : 0;
      if (this.submitBtn) this.submitBtn.tabIndex = collapsed ? -1 : 0;
    }

    /**
     * Handles Apply / Enter on the coupon form.
     * @param {SubmitEvent} evt
     */
    async handleSubmit(evt) {
      evt.preventDefault();
      if (this.updating) return;
      const code = this.input?.value.trim();

      if (!code) {
        this.showError(theme.strings.discountBlank);
        this.input?.focus();
        return;
      }

      const existing = this.existingCodes();
      if (existing.some((applied) => applied.toUpperCase() === code.toUpperCase())) {
        this.showError(theme.strings.discountAlreadyApplied);
        return;
      }

      await this.updateDiscounts([...existing, code], code);
    }

    /**
     * Handles clicks on remove-code buttons.
     * @param {MouseEvent} evt
     */
    handleClick(evt) {
      const removeBtn = evt.target.closest('[data-discount-code]');
      if (!removeBtn || this.updating) return;

      evt.preventDefault();
      const codeToRemove = removeBtn.getAttribute('data-discount-code');
      const remaining = this.existingCodes().filter(
        (code) => code.toUpperCase() !== codeToRemove.toUpperCase()
      );
      this.updateDiscounts(remaining);
    }

    /**
     * Applies or replaces cart discount codes, then refreshes the drawer.
     * @param {string[]} codes
     * @param {string} [submittedCode] - Code just entered, used to validate apply.
     */
    async updateDiscounts(codes, submittedCode) {
      this.updating = true;
      this.setLoading(true);
      this.clearError();

      try {
        const cartItems = this.cartItems();
        const sections = cartItems?.getSectionsToRender?.() || [];
        const body = {
          discount: codes.join(','),
          sections: [...new Set(sections.map((section) => section.section))],
          sections_url: window.location.pathname
        };

        const response = await fetch(CartDiscount.updateUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || response.status);

        if (submittedCode && !CartDiscount.isCodeApplicable(data, submittedCode)) {
          const message = await CartDiscount.discountErrorMessage(submittedCode, data);
          this.showError(message);
          this.input.focus();
          this.input.select();
          return;
        }

        this.renderCart(data, cartItems, sections);
        document.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: { cart: data }
        }));
        this.recalculateHeights();
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.showError(theme.strings.cartError);
      } finally {
        this.updating = false;
        this.setLoading(false);
      }
    }

    /**
     * Replaces cart drawer sections from an Ajax cart response.
     * @param {object} data
     * @param {CartItems|null} cartItems
     * @param {Array} sections
     */
    renderCart(data, cartItems, sections) {
      if (!data.sections || !cartItems || !sections.length) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        return;
      }

      sections.forEach((section) => {
        const sectionEl = document.getElementById(section.id);
        if (!sectionEl || !data.sections[section.section]) return;

        const el = sectionEl.querySelector(section.selector) || sectionEl;
        el.innerHTML = CartDiscount.getElementHTML(data.sections[section.section], section.selector);
      });
    }

    /**
     * Gets the innerHTML of an element inside a section HTML string.
     * @param {string} html
     * @param {string} selector
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const CartItemsEl = customElements.get('cart-items');
      if (CartItemsEl?.getElementHTML) return CartItemsEl.getElementHTML(html, selector);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * @returns {CartItems|null}
     */
    cartItems() {
      return this.closest('cart-drawer')?.querySelector('cart-items') || document.querySelector('cart-items');
    }

    /**
     * @param {boolean} loading
     */
    setLoading(loading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = loading;
      this.submitBtn.classList.toggle('is-loading', loading);
      this.submitBtn.setAttribute('aria-busy', String(loading));
    }

    /**
     * @param {string} message
     */
    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.recalculateHeights();
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    recalculateHeights() {
      const CartDrawerEl = customElements.get('cart-drawer');
      if (CartDrawerEl?.recalculateCssVarHeights) {
        CartDrawerEl.recalculateCssVarHeights();
      }
    }

    /**
     * @returns {string}
     */
    static updateUrl() {
      const url = theme.routes.cartUpdate;
      return url.endsWith('.js') ? url : `${url}.js`;
    }

    /**
     * Maps Shopify discount warning codes to a customer-facing message.
     * @param {string} code
     * @param {object} cart
     * @returns {Promise<string>}
     */
    static async discountErrorMessage(code, cart) {
      const strings = theme.strings || {};
      const fallback = strings.discountError
        || 'This coupon code is invalid or doesn’t apply to the items in your cart.';

      try {
        const warningCode = await CartDiscount.probeDiscountWarning(code, cart);
        if (warningCode) return CartDiscount.messageForWarning(warningCode);
      } catch (error) {
        console.log(error); // eslint-disable-line
      }

      return fallback;
    }

    /**
     * @param {string} warningCode
     * @returns {string}
     */
    static messageForWarning(warningCode) {
      const strings = theme.strings || {};
      const fallback = strings.discountError
        || 'This coupon code is invalid or doesn’t apply to the items in your cart.';

      switch (warningCode) {
        case 'DISCOUNT_NOT_FOUND':
        case 'DISCOUNT_CURRENTLY_INACTIVE':
        case 'DISCOUNT_CODE_NOT_HONOURED':
          return strings.discountInvalid || fallback;
        case 'DISCOUNT_NO_ENTITLED_LINE_ITEMS':
        case 'DISCOUNT_NO_ENTITLED_SHIPPING_LINES':
          return strings.discountNoEligible || fallback;
        case 'DISCOUNT_PURCHASE_NOT_IN_RANGE':
        case 'DISCOUNT_QUANTITY_NOT_IN_RANGE':
        case 'DISCOUNT_INCOMPATIBLE_PURCHASE_TYPE':
          return strings.discountRequirements || fallback;
        case 'DISCOUNT_CUSTOMER_NOT_ELIGIBLE':
        case 'DISCOUNT_CUSTOMER_USAGE_LIMIT_REACHED':
        case 'DISCOUNT_ELIGIBLE_CUSTOMER_MISSING':
        case 'DISCOUNT_USAGE_LIMIT_REACHED':
          return strings.discountCustomer || fallback;
        default:
          return fallback;
      }
    }

    /**
     * Creates a temporary Storefront cart to read discount warning codes.
     * @param {string} code
     * @param {object} cart
     * @returns {Promise<string|null>}
     */
    static async probeDiscountWarning(code, cart) {
      const token = theme.storefrontApi?.token;
      if (!token || !cart?.items?.length) return null;

      const url = CartDiscount.storefrontUrl();
      if (!url) return null;

      const lines = cart.items
        .filter((item) => item.variant_id)
        .map((item) => ({
          merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: Number(item.quantity) || 1
        }));
      if (!lines.length) return null;

      const query = `mutation CartDiscountProbe($input: CartInput!) {
        cartCreate(input: $input) {
          cart { discountCodes { code applicable } }
          warnings { code message }
          userErrors { code message }
        }
      }`;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 4000);

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          signal: controller.signal,
          body: JSON.stringify({
            query,
            variables: { input: { lines, discountCodes: [code] } }
          })
        });
        const payload = await response.json();
        const result = payload?.data?.cartCreate;
        if (!result) return null;

        const warnings = result.warnings || [];
        const discountWarning = warnings.find((warning) => (
          String(warning.code || '').startsWith('DISCOUNT_')
        ));
        if (discountWarning?.code) return discountWarning.code;

        const submitted = code.toUpperCase();
        const match = (result.cart?.discountCodes || []).find((entry) => (
          entry.code && entry.code.toUpperCase() === submitted
        ));
        if (match && match.applicable === false) return 'DISCOUNT_NO_ENTITLED_LINE_ITEMS';
        if (!match) return 'DISCOUNT_NOT_FOUND';
        return null;
      } finally {
        clearTimeout(timer);
      }
    }

    /**
     * Storefront GraphQL URL with a version that includes cart discount warnings.
     * @returns {string}
     */
    static storefrontUrl() {
      const url = theme.storefrontApi?.url;
      if (!url) return '';
      return url.replace(/\/api\/\d{4}-\d{2}\//, '/api/2025-07/');
    }

    /**
     * Whether a submitted code is active on the updated cart.
     * @param {object} cart
     * @param {string} code
     * @returns {boolean}
     */
    static isCodeApplicable(cart, code) {
      const submitted = code.toUpperCase();
      const discountCodes = cart.discount_codes || [];

      if (discountCodes.length) {
        const match = discountCodes.find((entry) => (
          entry.code && entry.code.toUpperCase() === submitted
        ));
        if (match) return match.applicable !== false;
      }

      const orderLevel = (cart.cart_level_discount_applications || []).some(
        (application) => application.title && application.title.toUpperCase() === submitted
      );
      if (orderLevel) return true;

      return (cart.items || []).some((item) => (
        (item.discounts || []).some((discount) => (
          discount.title && discount.title.toUpperCase() === submitted
        ))
      ));
    }
  }

  customElements.define('cart-discount', CartDiscount);
}
